import styled, { css, DefaultTheme } from 'styled-components'

import { TextFieldProps } from '.'

type IconProps = Pick<
  TextFieldProps,
  | 'icon'
  | 'iconColor'
  | 'iconBgColor'
  | 'iconPosition'
  | 'iconIsButton'
  | 'iconPosition'
  | 'radius'
>

type WrapperProps = Pick<TextFieldProps, 'disabled'> & { error?: boolean }

export const LabelWrapper = styled.div`
  position: relative;
  display: flex;
`

export const Label = styled.label<TextFieldProps>`
  ${({ theme, labelColor, labelSize, labelWeight, labelOverlapping }) => css`
    color: ${theme.ds.colors[labelColor!]};
    font-size: ${theme.ds.typography.size?.[labelSize!]};
    font-weight: ${theme.ds.typography.weight?.[labelWeight!]};
    line-height: ${theme.ds.typography.lineHeight.medium};
    cursor: pointer;
    margin-bottom: 8px;

    ${labelOverlapping &&
    css`
      position: absolute;
      z-index: 10;
      top: 0;
      left: 14px;
      transform: translateY(-50%);
      margin-bottom: 0px;
      padding: 0 4px;
      line-height: ${theme.ds.typography.lineHeight.small};
      background-color: ${theme.ds.colors.white};
    `}
  `}
`

export const LabelIcon = styled.div`
  display: flex;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
`

export const Asterisk = styled.span`
  font-size: ${({ theme }) => theme.ds.typography.size.small};
  font-weight: ${({ theme }) => theme.ds.typography.weight.normal};
  color: ${({ theme }) => theme.ds.colors.danger300};
  cursor: pointer;
`

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  ${({ theme }) => wrapperModifiers['normal'](theme)}
`

export const Input = styled.input<TextFieldProps>`
  ${({ theme, icon, iconPosition, bgColor, radius }) => css`
    width: 100%;
    height: 48px;
    padding-right: ${!!icon && iconPosition === 'insideRight'
      ? '56px'
      : '16px'};
    padding-left: ${!!icon && iconPosition === 'insideLeft' ? '56px' : '16px'};
    color: ${theme.ds.colors.neutral900};
    font-family: ${theme.ds.typography.fontFamily};
    font-size: ${theme.ds.typography.size.small};
    line-height: ${theme.ds.typography.lineHeight.medium};
    background-color: ${theme.ds.colors[bgColor!]};
    border: 1px solid ${theme.ds.colors.neutral300};
    border-radius: ${theme.ds.typography.border.radius[radius!]};
    outline: none;

    &::placeholder {
      color: ${theme.ds.colors.neutral500};
      font-weight: ${theme.ds.typography.weight.normal};
      opacity: 1;
    }

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
      opacity: 1;
    }
  `}
`

export const Icon = styled.button<IconProps>`
  ${({
    theme,
    iconPosition,
    iconIsButton,
    iconColor,
    iconBgColor,
    radius,
    disabled
  }) => {
    const inside =
      iconPosition === 'insideRight' || iconPosition === 'insideLeft'
    const iconLeft =
      iconPosition === 'insideLeft' || iconPosition === 'outsideLeft'

    return css`
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      order: ${iconLeft ? 0 : 1};
      height: 48px;
      width: 48px;
      position: ${inside ? 'absolute' : 'relative'} !important;
      top: ${inside ? '50%' : 'unset'};
      right: ${iconPosition === 'insideRight' ? 0 : 'unset'};
      left: ${iconPosition === 'insideLeft' ? 0 : 'unset'};
      transform: ${inside ? 'translateY(-50%)' : 'unset'};
      cursor: ${iconIsButton && !disabled ? 'pointer' : 'default'};
      outline: none;
      color: ${iconIsButton &&
      iconBgColor !== 'white' &&
      iconBgColor !== 'transparent' &&
      inside
        ? theme.ds.colors.white
        : theme.ds.colors[iconColor!]};
      background: ${iconIsButton && disabled && inside
        ? theme.ds.colors.primary400
        : iconIsButton && iconBgColor && inside
        ? theme.ds.colors[iconBgColor!]
        : 'none'};
      border: none;
      border-top: ${iconIsButton && inside
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-right: ${iconIsButton && iconPosition === 'insideRight'
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-bottom: ${iconIsButton && inside
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-left: ${iconIsButton && iconPosition === 'insideLeft'
        ? `1px solid ${theme.ds.colors.neutral300}`
        : 'none'};
      border-top-right-radius: ${iconPosition === 'insideRight'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-bottom-right-radius: ${iconPosition === 'insideRight'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-top-left-radius: ${iconPosition === 'insideLeft'
        ? theme.ds.typography.border.radius[radius!]
        : 0};
      border-bottom-left-radius: ${iconPosition === 'insideLeft'
        ? theme.ds.typography.border.radius[radius!]
        : 0};

      svg,
      path {
        max-width: 24px;
        max-height: 24px;
        fill: ${iconIsButton &&
        iconBgColor !== 'white' &&
        iconBgColor !== 'transparent' &&
        inside
          ? theme.ds.colors.white
          : theme.ds.colors[iconColor!]};
        opacity: 1;
      }
    `
  }}
`

export const Error = styled.p`
  margin-top: 4px;
  color: ${({ theme }) => theme.ds.colors.danger300};
  font-weight: ${({ theme }) => theme.ds.typography.weight.normal};
  line-height: ${({ theme }) => theme.ds.typography.lineHeight.medium};
  font-size: ${({ theme }) => theme.ds.typography.size.xsmall};
  text-align: left;
`

const wrapperModifiers = {
  normal: (theme: DefaultTheme) => css`
    ${Input} {
      &:focus {
        border-color: ${theme.ds.colors.primary300};
      }
    }
  `,
  error: (theme: DefaultTheme) => css`
    ${Input} {
      border-color: ${theme.ds.colors.danger200} !important;
    }
    ${Icon} {
      color: ${theme.ds.colors.danger200} !important;
      border-color: ${theme.ds.colors.danger200} !important;
    }
  `,
  disabled: (theme: DefaultTheme) => css`
    ${Input} {
      cursor: not-allowed;
      color: ${theme.ds.colors.neutral500};
      border-color: ${theme.ds.colors.neutral300};
      background-color: ${theme.ds.colors.neutral100};
    }
  `
}

export const Wrapper = styled.div<WrapperProps>`
  ${({ theme, error }) => error && wrapperModifiers.error(theme)}
  ${({ theme, disabled }) => disabled && wrapperModifiers.disabled(theme)}
`
